// Typed client for /api/followers (contract: packages/shared/src/features/followers.ts).

import type { ID, Page } from "@gmx/shared";
import type {
  FollowerEvent,
  FollowerItem,
  FollowerScan,
  FollowerScanKind,
  FollowersDigestStatus,
  FollowersListSort,
  FollowersRange,
  FollowersSettings,
  FollowersSummary,
  RelationPage,
  ToCrmRes,
} from "@gmx/shared/features/followers";
import { api } from "../../lib/api";

export type RelationKind = "not-following-back" | "fans";

export const followersKeys = {
  all: ["followers"] as const,
  summary: (igAccountId: ID, range: FollowersRange) => ["followers", "summary", igAccountId, range] as const,
  events: (igAccountId: ID, type: "gained" | "lost", q: string) => ["followers", "events", igAccountId, type, q] as const,
  list: (igAccountId: ID, status: "active" | "lost", sort: FollowersListSort, q: string) => ["followers", "list", igAccountId, status, sort, q] as const,
  relation: (igAccountId: ID, kind: RelationKind, q: string) => ["followers", "relation", igAccountId, kind, q] as const,
  scans: (igAccountId: ID) => ["followers", "scans", igAccountId] as const,
  settings: (igAccountId: ID) => ["followers", "settings", igAccountId] as const,
  digest: (igAccountId: ID) => ["followers", "digest", igAccountId] as const,
};

export const followersApi = {
  refresh: (igAccountId: ID) => api<{ scan: FollowerScan | null }>("/api/followers/refresh", { method: "POST", body: { igAccountId } }),
  summary: (igAccountId: ID, range: FollowersRange) => api<FollowersSummary>("/api/followers/summary", { query: { igAccountId, range } }),
  events: (igAccountId: ID, type: "gained" | "lost", q: string, cursor?: string | null) =>
    api<Page<FollowerEvent>>("/api/followers/events", { query: { igAccountId, type, q, cursor, limit: 50 } }),
  list: (igAccountId: ID, status: "active" | "lost", sort: FollowersListSort, q: string, cursor?: string | null) =>
    api<Page<FollowerItem>>("/api/followers/list", { query: { igAccountId, status, sort, q, cursor, limit: 50 } }),
  relation: (igAccountId: ID, kind: RelationKind, q: string, cursor?: string | null) =>
    api<RelationPage>(`/api/followers/${kind}`, { query: { igAccountId, q, cursor, limit: 50 } }),
  scans: (igAccountId: ID, limit = 5) => api<{ items: FollowerScan[]; running: FollowerScan[] }>("/api/followers/scans", { query: { igAccountId, limit } }),
  startScan: (igAccountId: ID, kind: FollowerScanKind) => api<{ scan: FollowerScan }>("/api/followers/scans", { method: "POST", body: { igAccountId, kind } }),
  cancelScan: (scanId: ID) => api<{ scan: FollowerScan }>(`/api/followers/scans/${encodeURIComponent(scanId)}/cancel`, { method: "POST", body: {} }),
  toCrm: (igAccountId: ID, igUserIds: string[]) => api<ToCrmRes>("/api/followers/to-crm", { method: "POST", body: { igAccountId, igUserIds } }),
  // Per X account (workspaces): always pass the selected account.
  settings: (igAccountId: ID) => api<FollowersSettings>("/api/followers/settings", { query: { igAccountId } }),
  saveSettings: (igAccountId: ID, patch: Partial<FollowersSettings>) =>
    api<FollowersSettings>("/api/followers/settings", { method: "PUT", body: patch, query: { igAccountId } }),
  digest: (igAccountId: ID) => api<FollowersDigestStatus>("/api/followers/digest", { query: { igAccountId } }),
  setDigest: (igAccountId: ID, enabled: boolean) => api<FollowersDigestStatus>("/api/followers/digest", { method: "PUT", body: { igAccountId, enabled } }),
};

/** Same-origin preview of the daily digest email (yesterday by default), opened in a new tab. */
export function digestPreviewUrl(igAccountId: ID): string {
  return `/api/followers/digest/preview?${new URLSearchParams({ igAccountId }).toString()}`;
}

/** Same-origin download URLs (cookie auth) for the streamed CSV exports. */
export function exportUrl(path: "events" | "list" | RelationKind, params: Record<string, string | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
  return `/api/followers/${path}/export.csv?${qs.toString()}`;
}
